// Helpery do eksportu danych tabeli WiseTable do pliku CSV.
//
// Strategia: czysto frontendowa - po kliku w "Eksport CSV" wywolujemy ten sam endpoint
// co tabela, z aktualnymi filtrami + limit=total_count, zeby dostac WSZYSTKIE wiersze. Z items
// budujemy CSV, podpinamy do <a download> i klikamy programowo.

/**
 * Konwertuje pojedyncza wartosc komorki do bezpiecznego stringu CSV.
 * - null/undefined -> pusty string
 * - boolean -> 'true'/'false'
 * - object (np. zagniezdzony) -> JSON.stringify (zeby nie zgubic kontekstu)
 * - string -> jak jest
 * - number/Date -> toString()
 *
 * Escapowanie wg RFC 4180: jesli wartosc zawiera separator/cudzyslow/newline,
 * caly value otaczamy " a wewnetrzne " zamieniamy na "".
 */
const escapeCsvValue = (value: unknown, separator: string): string => {
  if (value === null || value === undefined) {
    return '';
  }
  let str: string;
  if (typeof value === 'string') {
    str = value;
  } else if (typeof value === 'boolean' || typeof value === 'number') {
    str = String(value);
  } else if (value instanceof Date) {
    str = value.toISOString();
  } else if (typeof value === 'object') {
    try {
      str = JSON.stringify(value);
    } catch {
      str = String(value);
    }
  } else {
    str = String(value);
  }

  const needsQuoting =
    str.includes(separator) || str.includes('"') || str.includes('\n') || str.includes('\r');

  if (!needsQuoting) {
    return str;
  }
  return `"${str.replace(/"/g, '""')}"`;
};

export interface IBuildCsvOptions {
  /** Separator komorek (default `;` zeby Excel PL otworzyl bez konfiguracji). */
  separator?: string;
  /** Naglowki kolumn. Jesli pominiete - uzywa kluczy z pierwszego rekordu. */
  headers?: { key: string; label: string }[];
}

/**
 * Buduje string CSV z listy rekordow.
 * @param rows - lista obiektow z plaskimi kluczami (np. items z API)
 * @param options - separator + naglowki kolumn
 */
export const buildCsv = (rows: Record<string, unknown>[], options: IBuildCsvOptions = {}): string => {
  if (rows.length === 0) {
    return '';
  }

  const separator = options.separator ?? ';';
  const headers = options.headers ?? Object.keys(rows[0]).map((key) => ({ key, label: key }));

  const headerLine = headers.map((h) => escapeCsvValue(h.label, separator)).join(separator);
  const dataLines = rows.map((row) =>
    headers.map((h) => escapeCsvValue(row[h.key], separator)).join(separator)
  );

  // BOM dla UTF-8 nie jest tu dodawany w buildCsv - dodajemy go w downloadCsv przy tworzeniu Blob,
  // zeby ten helper byl uniwersalny (np. dla testow / preview).
  return [headerLine, ...dataLines].join('\r\n');
};

/**
 * Triggeruje download pliku CSV w przegladarce.
 * Dodaje BOM UTF-8 (U+FEFF) zeby Excel poprawnie czytal polskie znaki.
 *
 * @param csvContent - tresc pliku (np. z buildCsv)
 * @param fileName - nazwa pliku (np. 'klienci_2026-06-18.csv')
 */
export const downloadCsv = (csvContent: string, fileName: string): void => {
  // BOM UTF-8 (U+FEFF) — uzywamy escape sequence zamiast dosłownego znaku,
  // bo ESLint flaguje raw BOM jako "irregular whitespace".
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Cleanup obiektu URL po krotkim opoznieniu (zapewnia ze przegladarka zdazyla rozpoczac download).
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Buduje nazwe pliku z prefixem i bieząca data.
 * @example buildCsvFileName('klienci') -> 'klienci_2026-06-18.csv'
 */
export const buildCsvFileName = (prefix: string): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${prefix}_${yyyy}-${mm}-${dd}.csv`;
};
